'use client'

import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { useCreateDashboardSaveMutation } from '~/data/api/client/assistant'
import { logger } from '~/debug/logger'
import GenerativePanelCard from '~/features/generative-dashboard/GenerativePanelCard'

import type { Dashboard } from '../stream/dataParts'

type DashboardPartProps = {
  dashboard: Dashboard
  /** A shared conversation: the viewer sees the dashboard but does not save it. */
  readOnly: boolean
}

/** Panels that need room to read (charts, tables) take a whole row; KPI cards and donuts sit side by side. */
const FULL_WIDTH_PANELS = new Set(['line_chart', 'bar_chart', 'data_table', 'narrative'])

/**
 * A dashboard the assistant built. Its tiles show as placeholders until their data arrives; once every tile is
 * in, the operator can save the dashboard under a name.
 */
export default function DashboardPart({ dashboard, readOnly }: DashboardPartProps) {
  const t = useTranslations('features.AssistantV2')
  const { panels } = dashboard
  const isLoading = panels.some((panel) => panel.status === 'pending')

  return (
    <Paper variant="outlined" data-testid="assistant-dashboard" sx={{ p: 1.5 }}>
      <Stack spacing={1.5}>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))'
          }}
        >
          {panels.map((panel) => (
            <Box
              key={panel.panelId}
              data-testid="assistant-dashboard-panel"
              sx={{ gridColumn: FULL_WIDTH_PANELS.has(panel.panelType) ? '1 / -1' : 'auto', minWidth: 0 }}
            >
              <GenerativePanelCard panel={panel} />
            </Box>
          ))}
        </Box>
        {!readOnly && <SaveDashboard dashboard={dashboard} disabled={isLoading} defaultTitle={t('dashboardTitle')} />}
      </Stack>
    </Paper>
  )
}

function SaveDashboard({
  dashboard,
  disabled,
  defaultTitle
}: {
  dashboard: Dashboard
  disabled: boolean
  defaultTitle: string
}) {
  const t = useTranslations('features.AssistantV2')
  const [createSave, { isLoading }] = useCreateDashboardSaveMutation()
  const [naming, setNaming] = useState(false)
  const [title, setTitle] = useState(defaultTitle)
  const [state, setState] = useState<'idle' | 'saved' | 'failed'>('idle')

  const save = async () => {
    setState('idle')
    try {
      await createSave({
        dashboardSaveCreateParams: {
          title: title.trim() || defaultTitle,
          spec: { panels: dashboard.panels, order: dashboard.panels.map((panel) => panel.panelId) }
        }
      }).unwrap()
      setNaming(false)
      setState('saved')
    } catch (error) {
      logger.error('Failed to save an assistant dashboard', error)
      setState('failed')
    }
  }

  if (state === 'saved') {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary' }} role="status">
        {t('dashboardSaved')}
      </Typography>
    )
  }

  return (
    <Stack spacing={1}>
      {state === 'failed' && <Alert severity="error">{t('dashboardSaveError')}</Alert>}
      {naming ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            fullWidth
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            slotProps={{ htmlInput: { 'aria-label': t('dashboardName'), maxLength: 200 } }}
          />
          <Button size="small" color="inherit" disabled={isLoading} onClick={() => setNaming(false)}>
            {t('cancel')}
          </Button>
          <Button size="small" variant="contained" disabled={isLoading} onClick={() => void save()}>
            {t('save')}
          </Button>
        </Stack>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" disabled={disabled} onClick={() => setNaming(true)}>
            {t('saveDashboard')}
          </Button>
        </Box>
      )}
    </Stack>
  )
}
